import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BusMascot from '../components/BusMascot';
import api from '../services/api';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  
  // Compliance Report State
  const [complianceData, setComplianceData] = useState(null);
  const [complianceFilter, setComplianceFilter] = useState('all'); // all, handbook, arbitration

  const reportTypes = [
    {
      id: 'certifications',
      name: 'Certification Report',
      description: 'View all employee certifications and expiration dates',
      icon: '📜'
    },
    {
      id: 'fleet',
      name: 'Fleet Status Report',
      description: 'Current status of all vehicles in the fleet',
      icon: '🚌'
    },
    {
      id: 'maintenance',
      name: 'Maintenance Report',
      description: 'Vehicle maintenance history and upcoming service',
      icon: '🔧'
    },
    {
      id: 'employees',
      name: 'Employee Report',
      description: 'Employee roster with roles and departments',
      icon: '👥'
    },
    {
      id: 'expiring',
      name: 'Expiring Certifications',
      description: 'Certifications expiring in the next 30/60/90 days',
      icon: '⚠️'
    },
    {
      id: 'activity',
      name: 'Activity Log',
      description: 'System activity and audit trail',
      icon: '📊'
    },
    {
      id: 'compliance',
      name: 'Document Compliance',
      description: 'Who hasn\'t signed handbook/arbitration forms',
      icon: '✍️'
    }
  ];

  // Fetch compliance report data when selected
  useEffect(() => {
    if (selectedReport?.id === 'compliance') {
      fetchComplianceReport();
    }
  }, [selectedReport, complianceFilter]);

  const fetchComplianceReport = async () => {
    try {
      setLoading(true);
      const params = complianceFilter !== 'all' ? `?formSlug=${complianceFilter}` : '';
      const response = await api.get(`/compliance/report${params}`);
      setComplianceData(response.data);
    } catch (err) {
      console.error('Failed to fetch compliance report:', err);
      toast.error('Failed to load compliance report');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedReport) {
      toast.error('Please select a report type');
      return;
    }

    // For compliance report, data is already loaded
    if (selectedReport.id === 'compliance') {
      toast.success('Report data refreshed!');
      fetchComplianceReport();
      return;
    }

    setLoading(true);
    try {
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success(`${selectedReport.name} generated successfully!`);
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!complianceData?.notSignedEmployees) return;
    
    const headers = ['Last Name', 'First Name', 'Hire Date', 'Missing Forms'];
    const rows = complianceData.notSignedEmployees.map(emp => [
      emp.lastName,
      emp.firstName,
      emp.hireDate ? new Date(emp.hireDate).toLocaleDateString() : 'N/A',
      emp.missingForms.join('; ')
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported to CSV');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports</h1>
      </div>

      <div className="reports-layout">
        {/* Report Selection */}
        <div className="report-types">
          <h2>Select Report Type</h2>
          <div className="report-grid">
            {reportTypes.map(report => (
              <div 
                key={report.id}
                className={`report-card ${selectedReport?.id === report.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedReport(report);
                  setComplianceData(null);
                }}
              >
                <span className="report-icon">{report.icon}</span>
                <h3>{report.name}</h3>
                <p>{report.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Report Options */}
        <div className="report-options">
          <h2>Report Options</h2>
          
          {selectedReport ? (
            <div className="options-form">
              <div className="selected-report">
                <span className="report-icon">{selectedReport.icon}</span>
                <div>
                  <h3>{selectedReport.name}</h3>
                  <p>{selectedReport.description}</p>
                </div>
              </div>

              {/* Compliance Report Specific Options */}
              {selectedReport.id === 'compliance' ? (
                <div style={{ marginTop: '20px' }}>
                  <div className="form-group">
                    <label>Filter by Form</label>
                    <select
                      value={complianceFilter}
                      onChange={e => setComplianceFilter(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #2A2A2A', background: '#141414', color: 'white' }}
                    >
                      <option value="all">All Forms</option>
                      <option value="handbook">Employee Handbook Only</option>
                      <option value="arbitration">Arbitration Agreement Only</option>
                    </select>
                  </div>

                  {/* Summary Stats */}
                  {complianceData && (
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ marginBottom: '15px', color: '#FFFFFF' }}>Summary</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div style={{ background: 'rgba(34,197,94,0.1)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#4ADE80' }}>
                            {complianceData.summary.fullyCompliant}
                          </div>
                          <div style={{ fontSize: '12px', color: '#4ADE80' }}>Fully Compliant</div>
                        </div>
                        <div style={{ background: 'rgba(239,68,68,0.1)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#F87171' }}>
                            {complianceData.summary.notCompliant}
                          </div>
                          <div style={{ fontSize: '12px', color: '#F87171' }}>Not Compliant</div>
                        </div>
                      </div>
                      
                      {/* Compliance Rate Bar */}
                      <div style={{ marginTop: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontSize: '14px', color: '#A0A0A0' }}>Compliance Rate</span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#E0E0E0' }}>{complianceData.summary.complianceRate}%</span>
                        </div>
                        <div style={{ height: '10px', background: '#2A2A2A', borderRadius: '5px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${complianceData.summary.complianceRate}%`,
                            background: complianceData.summary.complianceRate >= 80 ? '#4ADE80' :
                                       complianceData.summary.complianceRate >= 50 ? '#D4A94E' : '#F87171',
                            transition: 'width 0.5s ease'
                          }} />
                        </div>
                      </div>

                      {/* Per-Form Stats */}
                      <div style={{ marginTop: '20px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#FFFFFF' }}>By Document</h4>
                        {complianceData.forms.map(form => (
                          <div key={form.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px',
                            background: '#1E1E1E',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}>
                            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>{form.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '14px', color: '#4ADE80' }}>{form.completed} signed</span>
                              <span style={{ fontSize: '14px', color: '#F87171' }}>{form.notStarted} missing</span>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                background: form.completionRate >= 80 ? 'rgba(34,197,94,0.1)' :
                                           form.completionRate >= 50 ? 'rgba(212,169,78,0.1)' : 'rgba(239,68,68,0.1)',
                                color: form.completionRate >= 80 ? '#4ADE80' :
                                       form.completionRate >= 50 ? '#D4A94E' : '#F87171'
                              }}>
                                {form.completionRate}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button 
                      className="btn-primary"
                      onClick={handleGenerateReport}
                      disabled={loading}
                      style={{ flex: 1 }}
                    >
                      {loading ? 'Loading...' : '🔄 Refresh Data'}
                    </button>
                    {complianceData && (
                      <button 
                        className="btn-secondary"
                        onClick={exportToCSV}
                        style={{ flex: 1 }}
                      >
                        📥 Export CSV
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Date Range (Optional)</label>
                    <div className="date-range">
                      <input 
                        type="date" 
                        value={dateRange.start}
                        onChange={e => setDateRange({...dateRange, start: e.target.value})}
                        placeholder="Start Date"
                      />
                      <span>to</span>
                      <input 
                        type="date" 
                        value={dateRange.end}
                        onChange={e => setDateRange({...dateRange, end: e.target.value})}
                        placeholder="End Date"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Format</label>
                    <select>
                      <option value="pdf">PDF</option>
                      <option value="excel">Excel</option>
                      <option value="csv">CSV</option>
                    </select>
                  </div>

                  <button 
                    className="btn-primary btn-large"
                    onClick={handleGenerateReport}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <BusMascot size={24} animated /> Generating...
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <BusMascot size={80} />
              <p>Select a report type to configure options</p>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Report Results */}
      {selectedReport?.id === 'compliance' && complianceData && (
        <div style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>❌ Employees Who Haven't Signed ({complianceData.notSignedEmployees.length})</h2>
          </div>
          
          {complianceData.notSignedEmployees.length === 0 ? (
            <div style={{
              background: 'rgba(34,197,94,0.1)',
              border: '2px solid #4ADE80',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '48px' }}>🎉</span>
              <h3 style={{ color: '#4ADE80', marginTop: '10px' }}>100% Compliance!</h3>
              <p style={{ color: '#4ADE80' }}>All employees have signed all required documents.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Hire Date</th>
                    <th>Missing Documents</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {complianceData.notSignedEmployees.map(emp => (
                    <tr key={emp.id}>
                      <td><strong>{emp.fullName}</strong></td>
                      <td>{formatDate(emp.hireDate)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {emp.forms.filter(f => f.status !== 'completed').map(form => (
                            <span key={form.formId} style={{
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: form.status === 'not_started' ? 'rgba(239,68,68,0.1)' : 'rgba(212,169,78,0.1)',
                              color: form.status === 'not_started' ? '#F87171' : '#D4A94E'
                            }}>
                              {form.formName.replace('Employee ', '').replace(' Agreement', '')}
                              {form.status === 'employee_signed' && ' (Pending HR)'}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: emp.missingCount === 2 ? 'rgba(239,68,68,0.1)' : 'rgba(212,169,78,0.1)',
                          color: emp.missingCount === 2 ? '#F87171' : '#D4A94E'
                        }}>
                          {emp.missingCount === 2 ? 'Not Started' : 'Partial'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <p style={{ fontSize: '12px', color: '#6B6B6B', marginTop: '15px' }}>
            Report generated: {new Date(complianceData.generatedAt).toLocaleString()}
          </p>
        </div>
      )}

      {/* Recent Reports */}
      <div className="recent-reports">
        <h2>Recent Reports</h2>
        <div className="empty-state">
          <p>No recent reports generated</p>
        </div>
      </div>
    </div>
  );
};

export default Reports;
